import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
};

export default function TyreBatteryTracking({ user, operatorDetails }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [conditions, setConditions] = useState([]);

    const [formData, setFormData] = useState({
        itemName: '',
        type: 'Tyre',
        quantity: '0',
        unitPrice: '0',
        totalPrice: '0',
        condition: '',
        notes: ''
    });

    useEffect(() => {
        fetchItems();
        fetchConditions();
    }, [user]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const opId = user?.operatorId || user?._id || 'all';
            const res = await axios.get(`${API}/tyre-batteries/${opId}`);
            setItems(res.data);
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchConditions = async () => {
        try {
            const res = await axios.get(`${API}/master-data`);
            const filtered = res.data.filter(d => d.category === 'item-conditions');
            setConditions(filtered);
            if (filtered.length > 0 && !formData.condition) {
                setFormData(prev => ({ ...prev, condition: filtered[0].name }));
            }
        } catch (err) {
            console.error('Fetch conditions error:', err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                operatorId: user?.operatorId || user?._id,
                quantity: parseInt(formData.quantity) || 0,
                unitPrice: parseFloat(formData.unitPrice) || 0,
                totalPrice: parseFloat(formData.totalPrice) || 0
            };
            if (editingItem) {
                await axios.put(`${API}/tyre-batteries/${editingItem._id}`, payload);
            } else {
                await axios.post(`${API}/tyre-batteries`, payload);
            }
            closeModal();
            fetchItems();
        } catch (err) {
            console.error('Save error:', err);
            alert("Failed to save item: " + (err.response?.data?.message || err.message));
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            itemName: item.itemName,
            type: item.type,
            quantity: item.quantity.toString(),
            unitPrice: (item.unitPrice || 0).toString(),
            totalPrice: (item.totalPrice || 0).toString(),
            condition: item.condition,
            notes: item.notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            await axios.delete(`${API}/tyre-batteries/${id}`);
            fetchItems();
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ itemName: '', type: 'Tyre', quantity: '0', unitPrice: '0', totalPrice: '0', condition: conditions[0]?.name || '', notes: '' });
    };

    return (
        <div className="tb-page">
            <style>{`
                .tb-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .tb-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .tb-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .tb-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .tb-add-btn {
                    background: #2563eb; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(37,99,235,0.2);
                }

                .tb-card { background: white; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 2px 4px rgba(0,0,0,0.02); overflow: hidden; }
                .tb-table { width: 100%; border-collapse: collapse; }
                .tb-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 700; 
                    color: #64748b; text-transform: uppercase; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
                }
                .tb-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                
                .tb-type-badge { padding: 4px 10px; border-radius: 6px; font-weight: 700; font-size: 12px; }
                .type-tyre { background: #fee2e2; color: #991b1b; }
                .type-battery { background: #dcfce7; color: #166534; }
                
                .condition-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; 
                    background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
                }

                /* Modal */
                .tb-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .tb-modal { background: white; width: 480px; border-radius: 20px; padding: 30px; position: relative; }
                .tb-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .tb-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                
                .tb-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
                .tb-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
                .tb-label { font-size: 12px; font-weight: 700; color: #475569; }
                .tb-label span { color: #ef4444; margin-left: 2px; }
                .tb-input, .tb-select, .tb-textarea {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s;
                }
                .tb-input:focus { border-color: #2563eb; }
                
                .tb-save-btn {
                    width: 100%; background: #6366f1; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; margin-top: 10px;
                }
                .tb-action-btn { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 4px; }
                .tb-action-btn:hover { background: #f1f5f9; color: #2563eb; }
                .tb-action-btn.del:hover { color: #ef4444; background: #fee2e2; }
            `}</style>

            <div className="tb-header">
                <div>
                    <h1 className="tb-title">Tyre & Battery Tracking</h1>
                    <p className="tb-subtitle">Monitor inventory levels and item conditions.</p>
                </div>
                <button className="tb-add-btn" onClick={() => setShowModal(true)}>
                    <Icon d={ic.plus} size={18} /> Add New Item
                </button>
            </div>

            <div className="tb-card">
                <table className="tb-table">
                    <thead>
                        <tr>
                            <th>Item Name</th>
                            <th>Type</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total Price</th>
                            <th>Condition</th>
                            <th>Last Updated</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40 }}>Loading inventory...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40 }}>No items found. Add a new one to get started.</td></tr>
                        ) : (
                            items.map(item => (
                                <tr key={item._id}>
                                    <td style={{ fontWeight: 600 }}>{item.itemName}</td>
                                    <td>
                                        <span className={`tb-type-badge ${item.type === 'Tyre' ? 'type-tyre' : 'type-battery'}`}>
                                            {item.type}
                                        </span>
                                    </td>
                                    <td>{item.quantity}</td>
                                    <td>{formatCurrency(item.unitPrice, operatorDetails?.country)}</td>
                                    <td style={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(item.totalPrice, operatorDetails?.country)}</td>
                                    <td><span className="condition-badge">{item.condition}</span></td>
                                    <td style={{ fontSize: 13, color: '#64748b' }}>{new Date(item.updatedAt).toLocaleDateString()}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className="tb-action-btn" onClick={() => handleEdit(item)}><Icon d={ic.edit} size={16} /></button>
                                            <button className="tb-action-btn del" onClick={() => handleDelete(item._id)}><Icon d={ic.trash} size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="tb-modal-overlay">
                    <div className="tb-modal">
                        <div className="tb-modal-close" onClick={closeModal}><Icon d={ic.x} size={20} /></div>
                        <h2 className="tb-modal-title">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
                        <form onSubmit={handleSave}>
                            <div className="tb-form-group">
                                <label className="tb-label">Item Name<span>*</span></label>
                                <input className="tb-input" placeholder="e.g., Michelin Tyre 205/55R16" required value={formData.itemName} onChange={e => setFormData({ ...formData, itemName: e.target.value })} />
                            </div>
                            <div className="tb-form-row">
                                <div className="tb-form-group">
                                    <label className="tb-label">Type<span>*</span></label>
                                    <select className="tb-select" required value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                                        <option value="Tyre">Tyre</option>
                                        <option value="Battery">Battery</option>
                                    </select>
                                </div>
                                <div className="tb-form-group">
                                    <label className="tb-label">Quantity<span>*</span></label>
                                    <input type="number" className="tb-input" required value={formData.quantity}
                                        onChange={e => {
                                            const qty = e.target.value;
                                            const up = formData.unitPrice;
                                            setFormData({ ...formData, quantity: qty, totalPrice: (parseFloat(qty) * parseFloat(up) || 0).toString() });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="tb-form-row">
                                <div className="tb-form-group">
                                    <label className="tb-label">Unit Price ({operatorDetails?.defaultCurrency || 'USD'})<span>*</span></label>
                                    <input type="number" step="0.01" className="tb-input" required value={formData.unitPrice}
                                        onChange={e => {
                                            const up = e.target.value;
                                            const qty = formData.quantity;
                                            setFormData({ ...formData, unitPrice: up, totalPrice: (parseFloat(qty) * parseFloat(up) || 0).toString() });
                                        }}
                                    />
                                </div>
                                <div className="tb-form-group">
                                    <label className="tb-label">Total Price</label>
                                    <input type="number" className="tb-input" readOnly value={formData.totalPrice} style={{ background: '#f8fafc' }} />
                                </div>
                            </div>
                            <div className="tb-form-group">
                                <label className="tb-label">Condition</label>
                                <select className="tb-select" required value={formData.condition} onChange={e => setFormData({ ...formData, condition: e.target.value })}>
                                    <option value="">Select Condition...</option>
                                    {conditions.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="tb-form-group">
                                <label className="tb-label">Notes</label>
                                <textarea className="tb-textarea" rows="3" placeholder="Additional information..." value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}></textarea>
                            </div>
                            <button type="submit" className="tb-save-btn">{editingItem ? 'Update Item' : 'Add Item'}</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
